"use client";

import type { SupSingleVaultV3, SupStoredFavToken } from "@/types";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import GridBarFiltersAtomTokens from "@/components/Grid/Bar/Filters/Atom/Tokens";
import GriBarFiltersAtomHeader from "@/components/Grid/Bar/Filters/Atom/Header";
import UiTag from "@/components/Ui/Tag";

import { useStoredData, useStoredDataDispatch } from "@/store/Context";
import { SupHide, SupShow, SupXCircle } from "@repo/core-ui";
import { twMerge } from "tailwind-merge";

interface LayoutSidebarFiltersTokenProps {
  vaults?: SupSingleVaultV3[];
}

export default function LayoutSidebarFiltersToken({ vaults }: LayoutSidebarFiltersTokenProps) {
  const storedData = useStoredData();
  const storedDispatch = useStoredDataDispatch();

  const [showSelector, toggleSelector] = useState(false);

  const selectedTokens = useMemo(() => (storedData.filters.find(storedFilter => storedFilter.name === "token")?.values ?? []) as SupStoredFavToken[], [storedData.filters]);

  useEffect(() => {
    if (selectedTokens.length === 0 && !showSelector) {
      toggleSelector(true);
    }
  }, [selectedTokens, showSelector]);

  const handleTokenSelection = useCallback(
    (newToken: SupStoredFavToken) => {
      const updatedTokenList =
        selectedTokens.find(singleToken => singleToken.chainId == newToken.chainId && singleToken.tokenAddress == newToken.tokenAddress) !== undefined
          ? [...selectedTokens.filter(singleToken => singleToken.chainId != newToken.chainId || singleToken.tokenAddress != newToken.tokenAddress)]
          : [...selectedTokens, newToken];
      storedDispatch({ type: "setFilter", filter: { name: "token", values: updatedTokenList } });
    },
    [selectedTokens, storedDispatch]
  );

  return (
    <GriBarFiltersAtomHeader name="token">
      <div className="flex flex-col">
        <ul className="scrollbar-hidden mt-[8px] flex max-h-[200px] w-full items-center overflow-x-scroll">
          {selectedTokens.map(singleToken => (
            <li key={`filter-token-${singleToken.tokenAddress}-${singleToken.chainId}`} className="sup-selected-token">
              <UiTag asset={{ icon: singleToken.tokenImage, symbol: `${singleToken.tokenSymbol}`, name: `${singleToken.tokenSymbol}` }} chainId={singleToken.chainId} isSmall />
              <SupXCircle className="cursor-pointer stroke-0 text-neutral hover:text-primary-200" onClick={() => handleTokenSelection(singleToken)} />
            </li>
          ))}
        </ul>
        <div
          className={twMerge(
            "group ml-[8px] flex h-[32px] w-[162px] cursor-pointer items-center space-x-[4px] border-[.5px] px-[8px] hover:text-primary-200" + " mt-[8px] hover:border-primary-200",
            showSelector && "bg-[rgba(195, 187, 236, 0.08)] border-primary-200 text-primary-200",
            selectedTokens.length === 0 && "hidden"
          )}
          onClick={() => toggleSelector(!showSelector)}
        >
          {!showSelector ? <SupShow className="stroke-0 group-hover:fill-primary-200" /> : <SupHide className="stroke-0 group-hover:fill-primary-200" />}
          <span className="mx-[10px] font-mono text-[12px] uppercase tracking-[.5px] group-hover:text-primary-200">Tokens</span>
        </div>
        {showSelector && <GridBarFiltersAtomTokens vaults={vaults} clickHandler={handleTokenSelection} selectedTokens={selectedTokens} />}
      </div>
    </GriBarFiltersAtomHeader>
  );
}
